/** App-wide UI palette (map view + global MUI theme). */
export const MAP_UI = {
  accent: '#607d8b',
  accentDark: '#546e7a',
  accentMuted: '#78909c',
  accentSoft: '#90a4ae',
  accentBg: 'rgba(96, 125, 139, 0.92)',
  accentBgLight: 'rgba(236, 239, 241, 0.95)',
  accentTint: 'rgba(96, 125, 139, 0.12)',
  accentTintHover: 'rgba(96, 125, 139, 0.2)',
  accentBorder: '#90a4ae',
  headerGradient: 'linear-gradient(135deg, #607d8b 0%, #78909c 100%)',
  headerGradientHover: 'linear-gradient(135deg, #546e7a 0%, #607d8b 100%)',
  cardTint: '#eceff1',
  moderate: '#78909c',
  conflict: '#90a4ae',
  heardByFill: '#b0bec5',
  heardByBorder: '#78909c',
  /** Map marker fills — clear green / soft red (not gray, not neon). */
  deviceOnline: '#66bb6a',
  deviceOnlineBorder: '#43a047',
  deviceOffline: '#f0abab',
  deviceOfflineBorder: '#e57373',
  /** RRM plan / selection on Public WiFi map (soft blue, not Material primary blue). */
  rrmPlanMarker: '#90caf9',
  rrmPlanMarkerActive: '#64b5f6',
  rrmPlanPolygonFill: '#90caf9',
  rrmPlanPolygonStroke: '#64b5f6',
  /** Unified accent for Public WiFi overview stat cards */
  publicWifiStat: '#78909c',
  /** iPerf / throughput legend swatches */
  throughputExcellent: '#43a047',
  throughputPoor: '#ef9a9a',
  onlineChipBg: '#e8f5e9',
  onlineChipText: '#2e7d32',
  onlineChipBorder: '#c8e6c9',
  offlineChipBg: '#ffebee',
  offlineChipText: '#c62828',
  offlineChipBorder: '#ffcdd2',
};

/** Summary stat cards (Public WiFi Devices + WiFi RRM). */
export const publicWifiStatSx = {
  card: { bgcolor: MAP_UI.accentTint },
  icon: { fontSize: 40, color: MAP_UI.publicWifiStat },
  value: { fontWeight: 700, color: MAP_UI.publicWifiStat, mb: 0.5 },
  planStatPaper: { p: 1.5, bgcolor: MAP_UI.accentTint },
  planStatCaption: { color: MAP_UI.publicWifiStat, opacity: 0.85 },
  planStatValue: { fontWeight: 600, color: MAP_UI.publicWifiStat },
};

/** Small “in plan / Public WiFi” chip on RRM tables */
export const rrmPlanChipSx = {
  fontSize: '0.65rem',
  height: 18,
  bgcolor: 'rgba(144, 202, 249, 0.35)',
  color: MAP_UI.rrmPlanMarkerActive,
  border: `1px solid ${MAP_UI.rrmPlanMarker}`,
};

/** View Device button on map info windows */
export const publicWifiMapActionBtnSx = {
  mt: 1,
  p: 0.5,
  px: 1.5,
  border: '1px solid',
  borderColor: MAP_UI.accent,
  borderRadius: 1,
  bgcolor: MAP_UI.accent,
  color: 'white',
  cursor: 'pointer',
  fontSize: '0.75rem',
  fontWeight: 500,
  '&:hover': { bgcolor: MAP_UI.accentDark },
};

/** Connection status dot (device list rows). */
export const statusDotSx = (connected) => ({
  bgcolor: connected ? MAP_UI.deviceOnline : MAP_UI.deviceOffline,
});

/** Soft filled chip for health / RSSI / latency (no bold MUI success/error fill). */
export const healthChipSoftSx = (variant) => {
  const labelSx = {
    height: 20,
    fontWeight: 600,
    border: '1px solid',
    '& .MuiChip-label': { px: 0.5, py: 0 },
  };
  if (variant === 'success') {
    return {
      ...labelSx,
      bgcolor: MAP_UI.onlineChipBg,
      color: MAP_UI.onlineChipText,
      borderColor: MAP_UI.onlineChipBorder,
    };
  }
  if (variant === 'error') {
    return {
      ...labelSx,
      bgcolor: MAP_UI.offlineChipBg,
      color: MAP_UI.offlineChipText,
      borderColor: MAP_UI.offlineChipBorder,
    };
  }
  if (variant === 'warning') {
    return {
      ...labelSx,
      bgcolor: MAP_UI.accentBgLight,
      color: MAP_UI.accentDark,
      borderColor: MAP_UI.accentBorder,
    };
  }
  return { height: 20, '& .MuiChip-label': { px: 0.5, py: 0 } };
};

/** @deprecated Global theme now uses MAP_UI; returns base theme unchanged. */
export function createMapDevicesTheme(baseTheme) {
  return baseTheme;
}

export function isDevicesMapViewPath(pathname, search = '') {
  if (pathname !== '/devices') return false;
  const viewMode = new URLSearchParams(search).get('viewMode') || 'map';
  return viewMode === 'map';
}
